import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { saveOnboardingProgress } from "@/lib/onboarding.functions";
import { useAuth } from "@/lib/auth-context";
import { COUNTRIES, INTERESTS } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvatarCropper } from "@/components/avatar-cropper";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2, MapPin, Tags, UserRound, Users } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { XamseenLogo } from "@/components/xamseen-logo";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Xamseen — Set up your account" },
      { name: "description", content: "Tell us where you're based and what you care about so Xamseen can show what's happening around you." },
      { property: "og:title", content: "Set up your Xamseen account" },
      { property: "og:description", content: "Tell us where you're based and what you care about." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OnboardingPage,
});

const TOTAL = 4;

type Community = { id: string; name: string; slug: string; member_count: number; country: string | null; city: string | null };

function OnboardingPage() {
  const { user, profile, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const save = useServerFn(saveOnboardingProgress);

  const [step, setStep] = useState(1);
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [area, setArea] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [joined, setJoined] = useState<string[]>([]);
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) navigate({ to: "/auth" }); }, [loading, user, navigate]);

  // Resume where the member left off if they closed the flow halfway.
  useEffect(() => {
    if (!profile) return;
    setCountry(profile.country ?? "");
    setRegion((profile as { region?: string | null }).region ?? "");
    setCity(profile.city ?? "");
    setArea(profile.neighborhood ?? "");
    setInterests(profile.interests ?? []);
    setFullName(profile.full_name ?? "");
    setUsername(profile.username ?? "");
    setBio(profile.bio ?? "");
    setAvatarUrl(profile.avatar_url ?? null);
    if (profile.onboarding_step > 0 && profile.onboarding_step < TOTAL) {
      setStep(Math.min(TOTAL, profile.onboarding_step + 1));
    }
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 3) return;
    let q = supabase
      .from("communities")
      .select("id, name, slug, member_count, country, city")
      .order("member_count", { ascending: false })
      .limit(8);
    const term = city.trim() || country.trim();
    if (term) q = q.or(`city.ilike.%${term}%,country.ilike.%${term}%,name.ilike.%${term}%`);
    q.then(({ data }) => setCommunities((data ?? []) as Community[]));
  }, [step, city, country]);

  const persist = async (patch: Parameters<typeof save>[0]["data"]) => {
    try {
      await save({ data: patch });
    } catch (e) {
      console.error(e);
      toast.error("We couldn't save that just now — you can continue and try again later.");
    }
  };

  const next = async () => {
    setBusy(true);
    if (step === 1) await persist({ step: 1, country: country || null, region: region || null, city: city || null, neighborhood: area || null });
    if (step === 2) await persist({ step: 2, interests });
    if (step === 3) await persist({ step: 3 });
    setBusy(false);
    setStep((s) => Math.min(TOTAL, s + 1));
  };

  const joinCommunity = async (c: Community) => {
    if (!user || joined.includes(c.id)) return;
    const { error } = await supabase.from("community_members").insert({ community_id: c.id, user_id: user.id, role: "member" });
    if (error) return toast.error(error.message);
    setJoined((j) => [...j, c.id]);
  };

  const uploadAvatar = async (file: Blob) => {
    if (!user) return;
    setUploading(true);
    const path = `${user.id}/${Date.now()}.jpg`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true, contentType: "image/jpeg" });
    if (error) { setUploading(false); toast.error(error.message); return; }
    const { data } = supabase.storage.from("avatars").getPublicUrl(path);
    setAvatarUrl(data.publicUrl);
    setUploading(false);
    setCropFile(null);
  };

  const finish = async () => {
    if (username && !/^[a-z0-9_]{3,24}$/.test(username)) {
      return toast.error("Username: 3–24 characters, letters, numbers or underscore.");
    }
    setBusy(true);
    try {
      await save({
        data: {
          step: TOTAL,
          complete: true,
          full_name: fullName.trim() || null,
          username: username || undefined,
          bio: bio.trim() || null,
          avatar_url: avatarUrl,
          country: country || null,
          region: region || null,
          city: city || null,
          neighborhood: area || null,
          interests,
        },
      });
    } catch (e) {
      setBusy(false);
      const msg = e instanceof Error && /duplicate|unique/i.test(e.message)
        ? "That username is already taken."
        : "We couldn't save your profile. Please try again.";
      return toast.error(msg);
    }
    await refreshProfile();
    setBusy(false);
    navigate({ to: "/" });
  };

  const skipToEnd = () => setStep(TOTAL);

  const countryLabel = useMemo(() => COUNTRIES.find((c) => c.name === country)?.name ?? country, [country]);
  const reduce = useReducedMotion();

  if (loading || !user) {
    return <div className="grid min-h-screen place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-background px-4 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto w-full max-w-[520px]">
        <XamseenLogo className="h-6" />

        <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          Welcome to Xamseen
        </p>
        <StepRail step={step} />

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={reduce ? undefined : { opacity: 0, y: 10 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-7"
          >
          {step === 1 && (
            <section>
              <h1 className="font-display text-[24px] font-semibold tracking-tight">Where are you based?</h1>
              <p className="mt-2 text-[15px] text-muted-foreground">
                This shapes your feed — you'll see what's happening near you first.
              </p>
              <div className="mt-6 grid gap-4">
                <div className="grid gap-1.5">
                  <Label>Country</Label>
                  <CountrySelect value={country} label={countryLabel} onChange={setCountry} />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ob-region" className="text-muted-foreground">Region, state or province <span className="font-normal">(optional)</span></Label>
                  <Input id="ob-region" className="h-11" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. Uusimaa, California, Lagos State" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ob-city">City or town</Label>
                  <Input id="ob-city" className="h-11" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Helsinki" />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="ob-area" className="text-muted-foreground">Local area <span className="font-normal">(optional)</span></Label>
                  <Input id="ob-area" className="h-11" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Neighbourhood, district or ward" />
                </div>
              </div>
            </section>
          )}

          {step === 2 && (
            <section>
              <h1 className="font-display text-[24px] font-semibold tracking-tight">What are you interested in?</h1>
              <p className="mt-2 text-[15px] text-muted-foreground">Pick as many as you like.</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {INTERESTS.map((tag) => {
                  const on = interests.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setInterests((p) => (on ? p.filter((t) => t !== tag) : [...p, tag]))}
                      className={cn(
                        "rounded-full border px-3.5 py-2 text-sm transition-colors",
                        on ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent",
                      )}
                    >
                      {on && <Check className="mr-1.5 inline h-3.5 w-3.5" />}
                      {tag}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {step === 3 && (
            <section>
              <h1 className="font-display text-[24px] font-semibold tracking-tight">Communities near you</h1>
              <p className="mt-2 text-[15px] text-muted-foreground">Follow a few to start, or skip this and explore later.</p>
              <div className="mt-6 grid gap-2">
                {communities.length === 0 && (
                  <p className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                    No communities found for that location yet. You can start one once you're inside.
                  </p>
                )}
                {communities.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{c.name}</p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="h-3 w-3" /> {c.member_count} members
                        {c.city ? ` · ${c.city}` : c.country ? ` · ${c.country}` : ""}
                      </p>
                    </div>
                    <Button size="sm" variant={joined.includes(c.id) ? "secondary" : "outline"} onClick={() => joinCommunity(c)}>
                      {joined.includes(c.id) ? "Joined" : "Join"}
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {step === 4 && (
            <section>
              <h1 className="font-display text-[24px] font-semibold tracking-tight">Complete your profile</h1>
              <p className="mt-2 text-[15px] text-muted-foreground">People trust real names and faces. Add what you're comfortable with.</p>
              <div className="mt-6 grid gap-5">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={avatarUrl ?? undefined} alt="" />
                    <AvatarFallback>{(fullName || "C").slice(0, 1).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <input
                      id="ob-avatar" type="file" accept="image/*" className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (!f) return;
                        if (f.size > 20 * 1024 * 1024) { toast.error("Choose a photo under 20MB"); return; }
                        setCropFile(f);
                      }}
                    />
                    <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                      <label htmlFor="ob-avatar" className="cursor-pointer">
                        {uploading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                        {avatarUrl ? "Change photo" : "Add photo"}
                      </label>
                    </Button>
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ob-name">Display name</Label>
                  <Input id="ob-name" className="h-11" value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={80} />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ob-username">Username</Label>
                  <Input
                    id="ob-username" className="h-11" value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                    maxLength={24}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ob-bio" className="text-muted-foreground">Bio <span className="font-normal">(optional)</span></Label>
                  <Textarea id="ob-bio" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={280} rows={3} />
                </div>
              </div>
            </section>
          )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-9 flex items-center gap-3">
          {step < TOTAL ? (
            <>
              <Button className="h-11 flex-1 font-medium" onClick={next} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Continue
              </Button>
              <Button variant="ghost" className="h-11" onClick={step === 3 ? next : skipToEnd}>
                Skip
              </Button>
            </>
          ) : (
            <Button className="h-11 w-full font-medium" onClick={finish} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enter Xamseen
            </Button>
          )}
        </div>
        {step > 1 && (
          <button type="button" onClick={() => setStep((s) => s - 1)} className="mt-4 text-sm text-muted-foreground hover:underline">
            Back
          </button>
        )}
      </div>

      <AvatarCropper
        open={cropFile !== null}
        file={cropFile}
        saving={uploading}
        onCancel={() => setCropFile(null)}
        onConfirm={uploadAvatar}
      />
    </div>
  );

}

function CountrySelect({ value, label, onChange }: { value: string; label: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="h-11 w-full justify-between font-normal">
          <span className={cn(!value && "text-muted-foreground")}>{value ? label : "Select your country"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search countries…" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem key={c.code} value={c.name} onSelect={() => { onChange(c.name); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === c.name ? "opacity-100" : "opacity-0")} />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const STEPS: { icon: typeof MapPin; label: string }[] = [
  { icon: MapPin, label: "Place" },
  { icon: Tags, label: "Interests" },
  { icon: Users, label: "Communities" },
  { icon: UserRound, label: "Profile" },
];

/** Sequence rail: icons and a filling connector, never numbered. */
function StepRail({ step }: { step: number }) {
  return (
    <div className="mt-5">
      <div className="flex items-center">
        {STEPS.map(({ icon: Icon, label }, i) => {
          const done = i + 1 < step;
          const active = i + 1 === step;
          return (
            <div key={label} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <span
                  className={cn(
                    "grid h-9 w-9 place-items-center rounded-xl border transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : done
                        ? "border-primary/40 bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground",
                  )}
                  aria-current={active ? "step" : undefined}
                >
                  {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </span>
                <span className={cn("text-[11px]", active ? "font-medium text-foreground" : "text-muted-foreground")}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <span className="mx-2 -mt-5 h-px flex-1 overflow-hidden rounded-full bg-border">
                  <span
                    className="block h-px bg-primary transition-[width] duration-500"
                    style={{ width: i + 1 < step ? "100%" : "0%" }}
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Everything here is optional — you can change it later in Settings.</p>
    </div>
  );
}
